import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { monthOptions, monthLabel } from "@/lib/dpc";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Layers } from "lucide-react";

function Catalog({ kind, title }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [group, setGroup] = useState("");
  const isSrc = kind === "sources";
  const load = useCallback(() => api.get("/dpc/meta").then(({ data }) => setItems(kind === "models" ? data.models : data.raw_sources)).catch(() => {}), [kind]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post(`/dpc/admin/${kind}`, isSrc ? { name, group: group || null } : { name }); toast.success(`${name} added`); setName(""); setGroup(""); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (n, active) => {
    try { await api.put(`/dpc/admin/${kind}/${encodeURIComponent(n)}`, { active }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const rename = async (n) => {
    const nn = window.prompt(`Rename "${n}" to:`, n);
    if (!nn || nn === n) return;
    try { await api.put(`/dpc/admin/${kind}/${encodeURIComponent(n)}`, { new_name: nn }); toast.success(`Renamed to ${nn} — plans & history updated`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const regroup = async (it) => {
    const g = window.prompt(`Report "${it.name}" under which group? (leave blank to report it on its own)`, it.group || "");
    if (g === null || (g.trim() || null) === (it.group || null)) return;
    try { await api.put(`/dpc/admin/${kind}/${encodeURIComponent(it.name)}`, { group: g.trim() || null }); toast.success(g.trim() ? `${it.name} now reports under ${g.trim()}` : `${it.name} reports on its own`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (n) => {
    if (!window.confirm(`Delete "${n}"? Only possible if it has no plan/achievement history.`)) return;
    try { await api.delete(`/dpc/admin/${kind}/${encodeURIComponent(n)}`); toast.success(`${n} deleted`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const groups = [...new Set(items.filter((i) => i.group).map((i) => i.group))];
  return (
    <div className="border bg-card rounded-md p-4 space-y-3" data-testid={`dpc-admin-${kind}`}>
      <h3 className="font-heading text-sm font-semibold">{title}</h3>
      <div className="flex gap-2 flex-wrap">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`New ${kind.slice(0, -1)}…`} className="h-8 text-sm w-48" data-testid={`dpc-add-${kind}-input`} />
        {isSrc && <Input value={group} onChange={(e) => setGroup(e.target.value)} placeholder="Group (optional, e.g. Connect)" list="dpc-source-groups" className="h-8 text-sm w-52" data-testid="dpc-add-sources-group-input" />}
        {isSrc && <datalist id="dpc-source-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>}
        <Button size="sm" onClick={add} disabled={!name.trim()} data-testid={`dpc-add-${kind}-btn`}>Add</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <span key={it.name} className={`flex items-center gap-1.5 rounded border px-2 py-1 text-xs ${it.active ? "" : "opacity-50 line-through"}`}>
            <Switch checked={it.active} onCheckedChange={(v) => toggle(it.name, v)} data-testid={`dpc-${kind}-toggle-${it.name.replace(/\s/g, "-")}`} />
            {it.name}
            {it.group && <span className="rounded bg-sky-100 text-sky-800 px-1.5 py-0.5 text-[10px] font-semibold" data-testid={`dpc-sources-group-${it.name.replace(/\s/g, "-")}`}>→ {it.group}</span>}
            {isSrc && <button onClick={() => regroup(it)} className="text-muted-foreground hover:text-foreground transition-colors duration-200" title="Set reporting group" data-testid={`dpc-sources-regroup-${it.name.replace(/\s/g, "-")}`}><Layers size={11} /></button>}
            <button onClick={() => rename(it.name)} className="text-muted-foreground hover:text-foreground transition-colors duration-200" title="Rename" data-testid={`dpc-${kind}-rename-${it.name.replace(/\s/g, "-")}`}><Pencil size={11} /></button>
            <button onClick={() => remove(it.name)} className="text-muted-foreground hover:text-red-600 transition-colors duration-200" title="Delete" data-testid={`dpc-${kind}-delete-${it.name.replace(/\s/g, "-")}`}><Trash2 size={11} /></button>
          </span>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Toggle = retire/reactivate (history kept) · pencil = rename (plans &amp; history follow) · bin = delete (only if unused).
        {isSrc && <> · layers = reporting group: sources in the same group are planned and reported as ONE heading (e.g. <b>Connect</b>), while the daily dump keeps its raw names.</>}
      </p>
    </div>
  );
}

function WeekConfig() {
  const [month, setMonth] = useState(monthOptions()[1]);
  const [weeks, setWeeks] = useState([]);
  const load = useCallback(() => api.get(`/dpc/meta?month=${month}`).then(({ data }) => setWeeks(data.weeks)).catch(() => {}), [month]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.put(`/dpc/admin/weeks/${month}`, { weeks }); toast.success(`Week boundaries saved for ${monthLabel(month)}`); } catch (e) { toast.error(apiError(e)); }
  };
  const reset = async () => {
    try { const { data } = await api.delete(`/dpc/admin/weeks/${month}`); setWeeks(data.weeks); toast.success("Reset to default"); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="border bg-card rounded-md p-4 space-y-3" data-testid="dpc-admin-weeks">
      <div className="flex items-center gap-3">
        <h3 className="font-heading text-sm font-semibold">Week boundaries</h3>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-7 w-[120px] text-xs" data-testid="dpc-weeks-month"><SelectValue /></SelectTrigger>
          <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap gap-3">
        {weeks.map((w, i) => (
          <div key={w.week} className="flex items-center gap-1 text-xs">
            <span className="font-semibold w-7">{w.week}</span>
            <Input type="number" min="1" max="31" value={w.start} className="h-7 w-14 text-xs" data-testid={`dpc-week-start-${w.week}`}
              onChange={(e) => setWeeks((ws) => ws.map((x, j) => (j === i ? { ...x, start: +e.target.value } : x)))} />
            <span>–</span>
            <Input type="number" min="1" max="31" value={w.end} className="h-7 w-14 text-xs" data-testid={`dpc-week-end-${w.week}`}
              onChange={(e) => setWeeks((ws) => ws.map((x, j) => (j === i ? { ...x, end: +e.target.value } : x)))} />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} data-testid="dpc-weeks-save">Save</Button>
        <Button size="sm" variant="outline" onClick={reset} data-testid="dpc-weeks-reset">Reset to default</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Weeks must be contiguous and cover the whole month (e.g. align W-boundaries with festival weeks).</p>
    </div>
  );
}

const DEFAULT_W = { retail: 35, booking: 15, test_drive: 10, enquiry: 10, lead: 5, conversion: 15, consistency: 5, compliance: 5 };
const W_LABELS = { retail: "Retail %", booking: "Booking %", test_drive: "Test Drive %", enquiry: "Enquiry %", lead: "Lead %", conversion: "Conversion quality", consistency: "Consistency", compliance: "Compliance" };

function DpcSettings() {
  const [s, setS] = useState(null);
  const [w, setW] = useState(null);
  useEffect(() => {
    api.get("/dpc/meta").then(({ data }) => {
      setS(data.settings);
      setW({ ...DEFAULT_W, ...(data.settings.dpi_weights || {}) });
    }).catch(() => {});
  }, []);
  const save = async () => {
    try {
      await api.put("/dpc/admin/settings", {
        plan_open_day: +s.plan_open_day, plan_due_day: +s.plan_due_day, cutoff: s.cutoff,
        week_share_threshold: +(s.week_share_threshold || 35),
        dpi_weights: Object.fromEntries(Object.entries(w).map(([k, v]) => [k, +v || 0])),
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!s || !w) return null;
  const wSum = Object.values(w).reduce((a, b) => a + (+b || 0), 0);
  return (
    <div className="border bg-card rounded-md p-4 space-y-4" data-testid="dpc-admin-settings">
      <h3 className="font-heading text-sm font-semibold">Plan cycle & compliance</h3>
      <div className="flex flex-wrap items-end gap-3 text-xs">
        <div>
          <label className="block mb-1 font-semibold text-muted-foreground">Plan window opens (day of prev month)</label>
          <Input type="number" min="1" max="28" value={s.plan_open_day} onChange={(e) => setS({ ...s, plan_open_day: e.target.value })} className="h-8 w-20 text-sm" data-testid="dpc-set-open" />
        </div>
        <div>
          <label className="block mb-1 font-semibold text-muted-foreground">Submission due (day of month)</label>
          <Input type="number" min="1" max="28" value={s.plan_due_day} onChange={(e) => setS({ ...s, plan_due_day: e.target.value })} className="h-8 w-20 text-sm" data-testid="dpc-set-due" />
        </div>
        <div>
          <label className="block mb-1 font-semibold text-muted-foreground">Upload cutoff (next day, IST)</label>
          <Input value={s.cutoff} onChange={(e) => setS({ ...s, cutoff: e.target.value })} className="h-8 w-24 text-sm" data-testid="dpc-set-cutoff" />
        </div>
        <div>
          <label className="block mb-1 font-semibold text-muted-foreground" title="Back-loading control: a plan is flagged when any week carries more than this share of the month plan (Retail or Enquiry), or W4 exceeds the W1–W3 average by 50 %. Flagged plans need a comment at every approval step.">Week-share flag (% of month in one week)</label>
          <Input type="number" min="1" max="100" value={s.week_share_threshold ?? 35} onChange={(e) => setS({ ...s, week_share_threshold: e.target.value })} className="h-8 w-20 text-sm" data-testid="dpc-set-week-threshold" />
        </div>
      </div>
      <div>
        <h4 className="text-xs font-semibold mb-2">DPI weights <span className={`num font-bold ${wSum === 100 ? "text-emerald-700" : "text-red-600"}`}>(sum {wSum}/100)</span></h4>
        <div className="flex flex-wrap gap-3 text-xs">
          {Object.keys(DEFAULT_W).map((k) => (
            <div key={k}>
              <label className="block mb-1 text-muted-foreground">{W_LABELS[k]}</label>
              <Input type="number" min="0" max="100" value={w[k]} onChange={(e) => setW({ ...w, [k]: e.target.value })} className="h-8 w-16 text-sm" data-testid={`dpc-weight-${k}`} />
            </div>
          ))}
        </div>
      </div>
      <Button size="sm" onClick={save} data-testid="dpc-settings-save">Save</Button>
    </div>
  );
}

export default function DpcAdmin() {
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Catalog kind="models" title="Models (Kia)" />
        <Catalog kind="sources" title="Lead sources" />
      </div>
      <WeekConfig />
      <DpcSettings />
    </div>
  );
}
